import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Key, CheckCircle2, XCircle, AlertTriangle, RefreshCw, TestTube2,
  Trash2, ExternalLink, ArrowLeft, ShieldAlert, ClipboardCheck,
} from "lucide-react";

/**
 * v6.1 Phase 1 — Settings > AI & Model Configuration > API Keys.
 *
 * Three provider cards (OpenAI, Anthropic, Google). Click a provider:
 *   - No key on file → shows entry form → validate + save
 *   - Key on file    → shows detail view with Test Key / Re-validate /
 *                       Replace / Delete
 *
 * Masking rule: only the last 4 characters of any saved key ever surface.
 */
function errMsg(e, fallback = "Something went wrong") {
  const d = e?.response?.data?.detail ?? e?.response?.data?.message ?? e?.response?.data;
  if (!d) return e?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join("; ");
  if (typeof d === "object") return d.message || d.msg || d.detail || fallback;
  return String(d);
}
export default function AIKeys() {
  const [providers, setProviders] = useState([]);
  const [creds, setCreds] = useState([]);
  const [selectedProviderId, setSelectedProviderId] = useState(null);

  const load = async () => {
    const [{ data: p }, { data: c }] = await Promise.all([
      api.get("/v61/providers"),
      api.get("/v61/credentials"),
    ]);
    setProviders(p);
    setCreds(c);
  };
  useEffect(() => { load(); }, []);

  const credFor = (pid) => creds.find((c) => c.provider_id === pid);

  if (selectedProviderId) {
    const prov = providers.find((p) => p.provider_id === selectedProviderId);
    const cred = credFor(selectedProviderId);
    return (
      <ProviderDetail
        provider={prov}
        credential={cred}
        onBack={() => { setSelectedProviderId(null); load(); }}
        onChanged={load}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="ai-keys-tab">
      <ModelAvailabilityPanel />
      <div className="cw-card p-5">
        <div className="cw-caption uppercase tracking-widest">
          Configure LLM Providers
        </div>
        <p className="text-sm text-[var(--cw-muted)] mt-1">
          Add your own API key for each provider you want to use. Keys are stored
          encrypted and only the last four characters are ever displayed.
          Choose a provider to add or manage its key.
        </p>
      </div>
      <div className="grid grid-cols-3 gap-3" data-testid="ai-keys-provider-grid">
        {providers.map((p) => {
          const c = credFor(p.provider_id);
          return (
            <button
              key={p.provider_id}
              onClick={() => setSelectedProviderId(p.provider_id)}
              className="cw-card p-4 text-left hover:shadow-md transition"
              data-testid={`ai-keys-provider-${p.provider_key}`}
            >
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center gap-2 font-semibold">
                  <Key size={14} /> {p.name}
                </div>
                <StatusPill status={c?.status} />
              </div>
              <div
                className="mt-2 font-mono text-xs text-[var(--cw-muted)]"
                data-testid={`ai-keys-mask-${p.provider_key}`}
              >
                {c ? c.masked_key : "No key on file"}
              </div>
              {c && c.available_model_count != null && (
                <div className="mt-1 text-xs text-[var(--cw-muted)]">
                  {c.available_model_count} model
                  {c.available_model_count === 1 ? "" : "s"} available
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  if (!status) {
    return (
      <span
        className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
        style={{ background: "rgba(0,0,0,0.06)", color: "var(--cw-muted)" }}
      >
        NOT SET
      </span>
    );
  }
  const map = {
    valid: { text: "VALID", bg: "rgba(44,154,82,0.15)", fg: "#1F6E3A" },
    invalid: { text: "INVALID", bg: "rgba(215,60,60,0.12)", fg: "#B02B2B" },
    unverified: { text: "UNVERIFIED", bg: "rgba(243,191,92,0.25)", fg: "#8A6510" },
    quota_exceeded: { text: "BILLING ISSUE", bg: "rgba(215,60,60,0.12)", fg: "#B02B2B" },
  };
  const s = map[status] || map.unverified;
  return (
    <span
      className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
      style={{ background: s.bg, color: s.fg }}
      data-testid={`status-pill-${status}`}
    >
      {s.text}
    </span>
  );
}

/* -------------------------- Provider Detail -------------------------- */
function ProviderDetail({ provider, credential, onBack, onChanged }) {
  const [entering, setEntering] = useState(!credential);
  const [replacing, setReplacing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // confirmDelete = null | { affected_agent_count }

  const revalidate = async () => {
    try {
      const { data } = await api.post(
        `/v61/credentials/${credential.credential_id}/revalidate`
      );
      toast.success(`Key valid — ${data.model_count} model${data.model_count === 1 ? "" : "s"} available`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation failed");
      onChanged();
    }
  };

  const testKey = async () => {
    try {
      const { data } = await api.post(`/v61/credentials/${credential.credential_id}/test`);
      if (data.ok) {
        toast.success(`Test succeeded — ${data.response_time_ms}ms`);
      } else {
        toast.error(data.failure_detail || "Test failed");
      }
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
      onChanged();
    }
  };

  const doDelete = async (confirm = false) => {
    try {
      const { data } = await api.delete(
        `/v61/credentials/${credential.credential_id}?confirm=${confirm}`
      );
      if (data.requires_confirmation) {
        setConfirmDelete({ affected_agent_count: data.affected_agent_count });
        return;
      }
      toast.success("Key deleted");
      onBack();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4">
      <button
        onClick={onBack}
        className="text-xs inline-flex items-center gap-1 text-[var(--cw-muted)] hover:text-[var(--cw-primary)]"
        data-testid="ai-keys-back"
      >
        <ArrowLeft size={12} /> All providers
      </button>
      <div className="cw-card p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="cw-caption uppercase tracking-widest">
              Provider
            </div>
            <h2 className="cw-h2 mt-1 inline-flex items-center gap-2">
              <Key size={18} /> {provider.name}
            </h2>
            {credential && (
              <div
                className="mt-2 font-mono text-sm"
                data-testid="ai-keys-detail-mask"
              >
                {credential.masked_key}
              </div>
            )}
            {credential && (
              <div className="mt-1 text-xs text-[var(--cw-muted)]">
                Last validated: {credential.last_validated_at || "—"} · Last tested:{" "}
                {credential.last_tested_at || "—"} · {credential.available_model_count ?? "—"} models
              </div>
            )}
          </div>
          {credential && <StatusPill status={credential.status} />}
        </div>

        {credential?.status === "quota_exceeded" && (
          <div
            className="mt-3 p-3 rounded flex items-start justify-between gap-3"
            style={{ background: "rgba(215,60,60,0.06)" }}
          >
            <div className="text-sm text-[#B02B2B]">
              <AlertTriangle size={14} className="inline mr-1" />
              This key is valid, but {provider.name}'s account behind it has run
              out of usage credit or hit a billing limit.
            </div>
            <a
              href={credential.billing_page_url || provider.billing_page_url}
              target="_blank"
              rel="noreferrer"
              className="cw-btn-primary text-xs inline-flex items-center gap-1 shrink-0"
              data-testid="ai-keys-reload-billing"
            >
              Reload / Add Credits <ExternalLink size={11} />
            </a>
          </div>
        )}

        {credential && !entering && !replacing && (
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={testKey}
              className="cw-btn-primary text-sm inline-flex items-center gap-1"
              data-testid="ai-keys-test"
            >
              <TestTube2 size={14} /> Test Key
            </button>
            <button
              onClick={revalidate}
              className="text-sm inline-flex items-center gap-1 rounded-md px-3 py-1.5 border"
              style={{ borderColor: "var(--cw-border)" }}
              data-testid="ai-keys-revalidate"
            >
              <RefreshCw size={14} /> Re-validate
            </button>
            <button
              onClick={() => setReplacing(true)}
              className="text-sm inline-flex items-center gap-1 rounded-md px-3 py-1.5 border"
              style={{ borderColor: "var(--cw-border)" }}
              data-testid="ai-keys-replace"
            >
              Replace Key
            </button>
            <a
              href={`/bulk-remap`}
              className="text-sm inline-flex items-center gap-1 rounded-md px-3 py-1.5 border"
              style={{ borderColor: "var(--cw-border)" }}
              data-testid="ai-keys-remap-all"
            >
              Remap All Agents
            </a>
            <button
              onClick={() => doDelete(false)}
              className="text-sm inline-flex items-center gap-1 rounded-md px-3 py-1.5 border ml-auto"
              style={{ borderColor: "var(--cw-error)", color: "var(--cw-error)" }}
              data-testid="ai-keys-delete"
            >
              <Trash2 size={14} /> Delete
            </button>
          </div>
        )}

        {(entering || replacing) && (
          <KeyEntryForm
            provider={provider}
            replacingCredentialId={replacing ? credential.credential_id : null}
            onCancel={() => { setEntering(false); setReplacing(false); }}
            onSaved={() => {
              setEntering(false); setReplacing(false); onChanged();
            }}
          />
        )}
      </div>

      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setConfirmDelete(null)}
          data-testid="ai-keys-delete-confirm"
        >
          <div
            className="cw-card p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="cw-h3 mb-2 inline-flex items-center gap-2">
              <AlertTriangle size={18} className="text-[#B02B2B]" /> Delete key?
            </h3>
            <p className="text-sm text-[var(--cw-muted)]">
              <strong>{confirmDelete.affected_agent_count}</strong> Agent
              {confirmDelete.affected_agent_count === 1 ? " is" : "s are"} currently
              using {provider.name}. Deleting this key will leave{" "}
              {confirmDelete.affected_agent_count === 1 ? "it" : "them"} in a Needs Attention
              state until they are remapped.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                className="cw-btn-secondary text-sm"
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </button>
              <button
                className="text-sm px-3 py-1.5 rounded"
                style={{ background: "var(--cw-error)", color: "white" }}
                onClick={() => { setConfirmDelete(null); doDelete(true); }}
                data-testid="ai-keys-delete-confirm-btn"
              >
                Delete anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------- Key Entry Form -------------------------- */
function KeyEntryForm({ provider, replacingCredentialId, onCancel, onSaved }) {
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [retryable, setRetryable] = useState(false);

const submit = async (saveAnyway = false) => {
  if (!apiKey.trim()) { setError("Please enter a key"); return; }
  setSaving(true);
  setError(null);
  try {
    const { data: resp } = replacingCredentialId
      ? await api.patch(`/v61/credentials/${replacingCredentialId}/replace`,
          { api_key: apiKey.trim(), save_anyway: saveAnyway })
      : await api.post("/v61/credentials", {
          provider_id: provider.provider_id,
          api_key: apiKey.trim(),
          save_anyway: saveAnyway,
        });

    if (resp?.ok === false) {
      setRetryable(!!resp.retryable);
      setError(typeof resp.message === "string" ? resp.message : "Key validation failed");
      return;
    }

    const n = resp?.model_count ?? 0;
    toast.success(`Key saved — ${n} model${n === 1 ? "" : "s"} available`);
    onSaved();
  } catch (e) {
    setError(errMsg(e, "Failed to save"));
  } finally {
    setSaving(false);
  }
};

  return (
    <div className="mt-4 space-y-2">
      <div className="cw-caption uppercase tracking-widest">
        {replacingCredentialId ? "Replace" : "Enter"} {provider.name} API Key
      </div>
      <input
        type="password"
        value={apiKey}
        onChange={(e) => { setApiKey(e.target.value); setError(null); setRetryable(false); }}
        placeholder={`${provider.name} API key`}
        className="w-full h-9 px-3 rounded-md border text-sm font-mono"
        style={{ borderColor: error ? "var(--cw-error)" : "var(--cw-border)" }}
        data-testid="ai-keys-entry-input"
        autoFocus
      />
      {error && (
        <div
          className="text-xs px-2 py-1.5 rounded"
          style={{ background: "rgba(215,60,60,0.08)", color: "#B02B2B" }}
          data-testid="ai-keys-entry-error"
        >
          {error}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button
          className="cw-btn-secondary text-sm"
          onClick={onCancel}
          data-testid="ai-keys-entry-cancel"
        >
          Cancel
        </button>
        {retryable && (
          <button
            className="text-sm inline-flex items-center gap-1 rounded-md px-3 py-1.5 border"
            style={{ borderColor: "var(--cw-border)" }}
            onClick={() => submit(true)}
            disabled={saving}
            data-testid="ai-keys-save-anyway"
          >
            Save Anyway
          </button>
        )}
        <button
          className="cw-btn-primary text-sm inline-flex items-center gap-1"
          onClick={() => submit(false)}
          disabled={saving}
          data-testid="ai-keys-save"
        >
          <CheckCircle2 size={13} /> {saving ? "Validating…" : "Validate & Save"}
        </button>
      </div>
    </div>
  );
}

/* -------------------------- Model Availability Panel -------------------------- */
function ModelAvailabilityPanel() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/v61/ai-settings");
    setSettings(data);
  };
  useEffect(() => { load(); }, []);

  if (!settings) return null;

  const toggle = async (checked) => {
    setBusy(true);
    try {
      await api.patch("/v61/ai-settings", {
        daily_availability_check_enabled: checked,
      });
      toast.success(checked ? "Daily check enabled" : "Daily check disabled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const checkNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/v61/ai-settings/check-now");
      const n = data.flipped_agents?.length || 0;
      toast.success(
        n === 0
          ? "All configured models still available"
          : `${n} agent${n === 1 ? "" : "s"} flagged Needs Attention`,
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Check failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="cw-card p-5" data-testid="ai-availability-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="cw-caption uppercase tracking-widest inline-flex items-center gap-1">
            <ShieldAlert size={12} /> Model Availability Checking
          </div>
          <p className="text-sm text-[var(--cw-muted)] mt-1 max-w-2xl">
            Each day we re-list every provider's models and flip any Agent whose
            model has disappeared to Needs Attention. Disabling this means a
            missing model will only be discovered the next time someone actually
            uses that Agent.
          </p>
          <div className="text-xs text-[var(--cw-muted)] mt-2">
            Last checked:{" "}
            <strong>
              {settings.last_daily_check_at
                ? new Date(settings.last_daily_check_at).toLocaleString()
                : "Never"}
            </strong>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-xs inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!settings.daily_availability_check_enabled}
              onChange={(e) => toggle(e.target.checked)}
              disabled={busy}
              data-testid="ai-availability-toggle"
            />
            Automatically check daily
          </label>
          <button
            onClick={checkNow}
            disabled={busy}
            className="cw-btn-primary text-sm inline-flex items-center gap-1"
            data-testid="ai-availability-check-now"
          >
            <ClipboardCheck size={13} /> {busy ? "Checking…" : "Check Now"}
          </button>
        </div>
      </div>
    </div>
  );
}
